import {
  Activity,
  Archive,
  Bell,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock3,
  Download,
  Eye,
  File,
  FileCheck2,
  FileSignature,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderKanban,
  Globe2,
  Grid2X2,
  HardDrive,
  History,
  KeyRound,
  LayoutDashboard,
  Link2,
  List,
  LockKeyhole,
  Menu,
  MessageSquareText,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { DocumentSummary, SpaceSummary } from "../shared/contracts";
import { useHaloId } from "./auth";
import { createClient } from "@supabase/supabase-js";

type SpaceApiRow = Omit<SpaceSummary, "updatedAt" | "sharePointUrl"> & {
  updatedAt: string;
  sharepointWebUrl?: string | null;
};

type ItemApiRow = {
  id: string;
  name: string;
  itemType: "file" | "folder";
  mimeType?: string | null;
  sizeBytes?: number | null;
  updatedAt: string;
  metadata?: Record<string, unknown>;
};

function formatBytes(value?: number | null) {
  if (!value) return "—";
  return new Intl.NumberFormat("fr-CA", {
    style: "unit",
    unit: value >= 1_000_000 ? "megabyte" : "kilobyte",
    unitDisplay: "short",
    maximumFractionDigits: 1,
  }).format(value >= 1_000_000 ? value / 1_000_000 : value / 1_000);
}

function documentType(item: ItemApiRow): DocumentSummary["type"] {
  if (item.itemType === "folder") return "folder";
  if (item.name.toLowerCase().endsWith(".docx")) return "docx";
  if (item.name.toLowerCase().endsWith(".xlsx")) return "xlsx";
  return "pdf";
}

function initials(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "K";
}

type Modal = "invite" | "upload" | null;

const statusLabel = {
  approved: "Approuvé",
  review: "À réviser",
  draft: "Brouillon",
  signed: "Signé",
};

function Brand({ light = false }: { light?: boolean }) {
  return (
    <div className="brand" aria-label="KOLIVO eDocs">
      <img
        className="brand-wordmark"
        src={
          light
            ? "/kolivo-vault-wordmark-accent-tm.svg"
            : "/kolivo-vault-wordmark-white-accent-tm.svg"
        }
        alt="KOLIVO Vault"
      />
      <span className="brand-product">eDocs</span>
    </div>
  );
}

function FileIcon({ type }: { type: DocumentSummary["type"] }) {
  if (type === "folder") return <Folder aria-hidden="true" />;
  if (type === "xlsx") return <FileSpreadsheet aria-hidden="true" />;
  if (type === "docx") return <FileText aria-hidden="true" />;
  return <File aria-hidden="true" />;
}

function Sidebar({
  adminMode,
  onAdminMode,
  open,
  onClose,
  userName,
}: {
  adminMode: boolean;
  onAdminMode: (value: boolean) => void;
  open: boolean;
  onClose: () => void;
  userName: string;
}) {
  const nav = adminMode
    ? [
        [LayoutDashboard, "Vue d’ensemble"],
        [Building2, "Espaces clients"],
        [Users, "Accès externes"],
        [FileCheck2, "Approbations"],
        [FileSignature, "Signatures"],
        [Activity, "Audit et conformité"],
        [SlidersHorizontal, "Modèles et règles"],
      ]
    : [
        [LayoutDashboard, "Accueil"],
        [FolderKanban, "Mes documents"],
        [FileCheck2, "À approuver"],
        [MessageSquareText, "Commentaires"],
        [History, "Activité"],
      ];
  return (
    <>
      {open && <button className="scrim" aria-label="Fermer le menu" onClick={onClose} />}
      <aside className={`sidebar ${open ? "is-open" : ""}`}>
        <Brand />
        <div className="mode-switch" role="group" aria-label="Mode d’affichage">
          <button
            className={!adminMode ? "active" : ""}
            onClick={() => onAdminMode(false)}
          >
            Client
          </button>
          <button
            className={adminMode ? "active" : ""}
            onClick={() => onAdminMode(true)}
          >
            Administration
          </button>
        </div>
        <nav aria-label="Navigation principale">
          <div className="nav-label">
            {adminMode ? "Gérer eDocs" : "Mon espace"}
          </div>
          {nav.map(([Icon, label], index) => (
            <button
              className={`nav-item ${index === 1 ? "active" : ""}`}
              key={label as string}
              onClick={onClose}
            >
              <Icon size={18} aria-hidden="true" />
              <span>{label as string}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button className="nav-item">
            <CircleHelp size={18} />
            <span>Centre d’aide</span>
          </button>
          <button className="nav-item">
            <Settings size={18} />
            <span>Paramètres</span>
          </button>
          <div className="user-card">
            <div className="avatar">{initials(userName)}</div>
            <div>
              <strong>{userName}</strong>
              <span>HaloID</span>
            </div>
            <MoreHorizontal size={18} />
          </div>
        </div>
      </aside>
    </>
  );
}

function ModalShell({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onMouseDown={event => event.stopPropagation()}
      >
        <button className="icon-button modal-close" onClick={onClose} aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="modal-icon">
          <Sparkles size={20} />
        </div>
        <h2 id="modal-title">{title}</h2>
        <p>{description}</p>
        {children}
      </section>
    </div>
  );
}

function SpaceModal({ onClose }: { onClose: () => void }) {
  const [created, setCreated] = useState(false);
  const [loginRequired, setLoginRequired] = useState(true);
  const [locationRequired, setLocationRequired] = useState(false);
  return (
    <ModalShell
      title="Créer un espace client"
      description="eDocs préparera la bibliothèque SharePoint, les dossiers, les règles de rétention et le lien dans Gate."
      onClose={onClose}
    >
      {created ? (
        <div className="success-state">
          <div className="success-check"><Check size={26} /></div>
          <h3>Demande de création préparée</h3>
          <p>
            En production, le statut sera suivi automatiquement dans Gate jusqu’à
            la confirmation de SharePoint.
          </p>
          <button className="primary-button" onClick={onClose}>Terminer</button>
        </div>
      ) : (
        <form
          className="form-grid"
          onSubmit={event => {
            event.preventDefault();
            setCreated(true);
          }}
        >
          <label>
            Identifiant client Gate
            <input required />
          </label>
          <label>
            Nom de l’espace
            <input required />
          </label>
          <label>
            Modèle documentaire
            <select defaultValue="standard">
              <option value="standard">Client standard</option>
              <option value="board">Conseil d’administration</option>
              <option value="audit">Audit et conformité</option>
            </select>
          </label>
          <label>
            Rétention
            <select defaultValue="7">
              <option value="7">7 ans</option>
              <option value="10">10 ans</option>
              <option value="project">Durée du projet + 2 ans</option>
            </select>
          </label>
          <fieldset className="access-policy">
            <legend>Qui peut ouvrir cet espace?</legend>
            <label>
              <input type="radio" name="login-policy" checked={loginRequired} onChange={() => setLoginRequired(true)} />
              Connexion HaloID obligatoire
            </label>
            <label>
              <input type="radio" name="login-policy" checked={!loginRequired} onChange={() => setLoginRequired(false)} />
              Partenaires sans HaloID
            </label>
          </fieldset>
          {loginRequired ? (
            <label className="full-row">
              Utilisateurs HaloID autorisés
              <button type="button" className="directory-picker">
                <Users size={16} /> Choisir dans l’annuaire HaloID
              </button>
              <small>La liste provient directement de HaloID; aucune saisie libre.</small>
            </label>
          ) : (
            <div className="policy-note full-row">
              <img src="/kolivo-vault-mark.svg" alt="" />
              <span>Le portail externe utilise le logo KOLIVO™ Vault officiel et permet lien simple, code ou MFA.</span>
            </div>
          )}
          <label className="location-policy full-row">
            <input type="checkbox" checked={locationRequired} onChange={event => setLocationRequired(event.target.checked)} />
            <span>
              <strong>Localisation précise obligatoire</strong>
              <small>Bloquer l’ouverture sans activation explicite et position d’une précision maximale de 100 mètres.</small>
            </span>
          </label>
          {locationRequired && (
            <label className="full-row">
              Justification réglementaire ou contractuelle
              <textarea required placeholder="Référence du contrôle, de la politique ou du mandat applicable" />
            </label>
          )}
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={onClose}>Annuler</button>
            <button type="submit" className="primary-button">Créer l’espace</button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function InviteModal({ spaceId, onClose }: { spaceId: string; onClose: () => void }) {
  const { accessToken } = useHaloId();
  const [invitation, setInvitation] = useState<{ magicLink: string; code?: string }>();
  const [mode, setMode] = useState("magic_link_code");
  const [error, setError] = useState("");
  return (
    <ModalShell
      title="Inviter une personne externe"
      description="Aucun compte HaloID requis. Choisissez le niveau de vérification adapté au document."
      onClose={onClose}
    >
      {invitation ? (
        <div className="secret-result">
          <div className="security-inline">
            <ShieldCheck size={20} />
            <div><strong>Accès limité à 48 heures</strong><span>1 utilisation · téléchargement bloqué · watermark activé</span></div>
          </div>
          <label>Lien magique<input readOnly value={invitation.magicLink} /></label>
          {invitation.code && <label>Code à transmettre séparément<input readOnly value={invitation.code} /></label>}
          <button className="primary-button" onClick={onClose}>J’ai sauvegardé les accès</button>
        </div>
      ) : (
        <form
          className="form-grid"
          onSubmit={event => {
            event.preventDefault();
            setError("");
            const form = new FormData(event.currentTarget);
            void (async () => {
              const token = await accessToken();
              const response = await fetch("/api/v1/admin/invitations", {
                method: "POST",
                headers: {
                  authorization: `Bearer ${token}`,
                  "content-type": "application/json",
                },
                body: JSON.stringify({
                  spaceId,
                  displayName: String(form.get("displayName") || ""),
                  email: String(form.get("email") || ""),
                  role: String(form.get("role") || "viewer"),
                  expiresInMinutes: Number(form.get("expiresInMinutes") || 2880),
                  maxUses: 1,
                  accessMode: mode,
                  phone: mode === "mfa_sms" ? String(form.get("phone") || "") : undefined,
                  watermark: form.get("watermark") === "on",
                  allowDownload: form.get("allowDownload") === "on",
                  allowPrint: form.get("allowPrint") === "on",
                }),
              });
              if (!response.ok) throw new Error("invitation_failed");
              const result = (await response.json()) as { magicLink: string; code?: string };
              setInvitation(result);
            })().catch(() => setError("L’invitation réelle n’a pas pu être créée. Aucun faux lien n’a été généré."));
          }}
        >
          <label>Nom complet<input name="displayName" required autoComplete="name" /></label>
          <label>Courriel<input name="email" required type="email" autoComplete="email" /></label>
          <label>Niveau d’accès<select name="role" defaultValue="viewer"><option value="viewer">Lecture seulement</option><option value="reviewer">Révision et commentaires</option><option value="contributor">Contribution</option></select></label>
          <label>Expiration<select name="expiresInMinutes" defaultValue="2880"><option value="1440">24 heures</option><option value="2880">48 heures</option><option value="10080">7 jours</option></select></label>
          <label className="full-row">
            Vérification d’accès
            <select value={mode} onChange={event => setMode(event.target.value)}>
              <option value="secure_link">Lien sécurisé sans MFA</option>
              <option value="magic_link_code">Lien magique + code</option>
              <option value="mfa_email">Lien + MFA par courriel</option>
              <option value="mfa_sms">Lien + MFA par SMS</option>
            </select>
          </label>
          {mode === "mfa_sms" && (
            <label className="full-row">
              Téléphone mobile
              <input name="phone" required type="tel" autoComplete="tel" />
            </label>
          )}
          <div className="permission-row">
            <label><input name="watermark" type="checkbox" defaultChecked /> Watermark nominatif</label>
            <label><input name="allowDownload" type="checkbox" /> Autoriser le téléchargement</label>
            <label><input name="allowPrint" type="checkbox" /> Autoriser l’impression</label>
          </div>
          {error && <p className="access-message error full-row">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={onClose}>Annuler</button>
            <button type="submit" className="primary-button">Créer l’invitation</button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function UploadModal({
  spaceId,
  onClose,
}: {
  spaceId: string;
  onClose: () => void;
}) {
  const { accessToken } = useHaloId();
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState<"idle" | "uploading" | "done" | "error">("idle");

  async function upload(file: File) {
    setState("uploading");
    setProgress(1);
    try {
      const token = await accessToken();
      const response = await fetch(`/api/v1/spaces/${spaceId}/uploads`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || "application/octet-stream",
          parentItemId: "root",
        }),
      });
      if (!response.ok) throw new Error("upload_session_failed");
      const session = (await response.json()) as
        | {
            provider: "s3";
            sessionId: string;
            partSize: number;
          }
        | {
            provider: "supabase";
            bucket: string;
            objectPath: string;
            token: string;
        }
        | { uploadUrl: string };
      if ("provider" in session && session.provider === "s3") {
        const partCount = Math.ceil(file.size / session.partSize);
        const partNumbers = Array.from({ length: partCount }, (_, index) => index + 1);
        const completed: Array<{
          ETag: string;
          PartNumber: number;
          ChecksumSHA256: string;
        }> = [];
        let completedBytes = 0;
        const workers = Array.from({ length: Math.min(4, partCount) }, async () => {
          while (partNumbers.length) {
            const partNumber = partNumbers.shift();
            if (!partNumber) return;
            const start = (partNumber - 1) * session.partSize;
            const end = Math.min(start + session.partSize, file.size);
            const chunk = file.slice(start, end);
            const digest = await crypto.subtle.digest("SHA-256", await chunk.arrayBuffer());
            const checksumSHA256 = btoa(
              String.fromCharCode(...new Uint8Array(digest))
            );
            const signResponse = await fetch(
              `/api/v1/uploads/${session.sessionId}/parts/${partNumber}`,
              {
                method: "POST",
                headers: {
                  authorization: `Bearer ${token}`,
                  "content-type": "application/json",
                },
                body: JSON.stringify({ checksumSHA256 }),
              }
            );
            if (!signResponse.ok) throw new Error("part_signature_failed");
            const { url } = (await signResponse.json()) as { url: string };
            const uploaded = await fetch(url, {
              method: "PUT",
              headers: { "x-amz-checksum-sha256": checksumSHA256 },
              body: chunk,
            });
            const etag = uploaded.headers.get("etag");
            if (!uploaded.ok || !etag) throw new Error("upload_part_failed");
            completed.push({
              ETag: etag,
              PartNumber: partNumber,
              ChecksumSHA256: checksumSHA256,
            });
            completedBytes += chunk.size;
            setProgress(Math.round((completedBytes / file.size) * 96));
          }
        });
        await Promise.all(workers);
        const completeResponse = await fetch(
          `/api/v1/uploads/${session.sessionId}/complete`,
          {
            method: "POST",
            headers: {
              authorization: `Bearer ${token}`,
              "content-type": "application/json",
            },
            body: JSON.stringify({ parts: completed }),
          }
        );
        if (!completeResponse.ok) throw new Error("upload_complete_failed");
        setProgress(100);
      } else if ("provider" in session && session.provider === "supabase") {
        const configResponse = await fetch("/api/v1/public/config");
        const publicConfig = (await configResponse.json()) as {
          supabase: null | { url: string; anonKey: string };
        };
        if (!publicConfig.supabase) throw new Error("supabase_not_configured");
        const storageClient = createClient(
          publicConfig.supabase.url,
          publicConfig.supabase.anonKey,
          { auth: { persistSession: false } }
        );
        setProgress(15);
        const { error } = await storageClient.storage
          .from(session.bucket)
          .uploadToSignedUrl(session.objectPath, session.token, file, {
            contentType: file.type || "application/octet-stream",
          });
        if (error) throw error;
        setProgress(100);
      } else {
        const chunkSize = 10 * 1024 * 1024;
        for (let start = 0; start < file.size; start += chunkSize) {
          const end = Math.min(start + chunkSize, file.size);
          const part = await fetch(session.uploadUrl, {
            method: "PUT",
            headers: {
              "content-length": String(end - start),
              "content-range": `bytes ${start}-${end - 1}/${file.size}`,
            },
            body: file.slice(start, end),
          });
          if (!part.ok && part.status !== 202) throw new Error("upload_chunk_failed");
          setProgress(Math.round((end / file.size) * 100));
        }
      }
      setState("done");
    } catch {
      setState("error");
    }
  }

  return (
    <ModalShell
      title="Téléverser des documents"
      description="Téléversement multipart chiffré, reprenable et versionné dans Amazon S3 Canada."
      onClose={onClose}
    >
      <label className="dropzone">
        <Upload size={28} />
        <strong>Choisir un fichier</strong>
        <span>PDF, Office et images · jusqu’à 250 Go</span>
        <input
          type="file"
          disabled={state === "uploading"}
          onChange={event => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
          }}
        />
      </label>
      {state !== "idle" && (
        <div className={`upload-progress ${state}`} aria-live="polite">
          <span style={{ width: `${progress}%` }} />
          <strong>
            {state === "done"
              ? "Téléversement terminé"
              : state === "error"
                ? "Le téléversement a échoué"
                : `${progress} %`}
          </strong>
        </div>
      )}
    </ModalShell>
  );
}

function ConsentPanel({ onClose }: { onClose: () => void }) {
  const { accessToken } = useHaloId();
  const [preferences, setPreferences] = useState({
    analytics: false,
    heatmaps: false,
    replay: false,
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      const token = await accessToken();
      const response = await fetch("/api/v1/privacy/consent", {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          analytics: preferences.analytics,
          heatmaps: preferences.heatmaps,
          sessionReplay: preferences.replay,
          policyVersion: "2026-07-26",
        }),
      });
      if (!response.ok) throw new Error("consent_failed");
      onClose();
    } finally {
      setSaving(false);
    }
  }
  return (
    <section className="consent-panel" aria-label="Préférences de confidentialité">
      <button className="icon-button consent-close" onClick={onClose} aria-label="Fermer">
        <X size={18} />
      </button>
      <ShieldCheck size={24} />
      <div className="consent-copy">
        <strong>Votre confidentialité d’abord</strong>
        <p>
          Le contenu et les champs sensibles ne sont jamais enregistrés. Les mesures
          d’utilisation facultatives restent désactivées sans votre accord.
        </p>
      </div>
      <div className="consent-options">
        {[
          ["analytics", "Mesure d’utilisation"],
          ["heatmaps", "Carte de clics"],
          ["replay", "Relecture de session masquée"],
        ].map(([key, label]) => (
          <label key={key}>
            <span>{label}</span>
            <input
              type="checkbox"
              checked={preferences[key as keyof typeof preferences]}
              onChange={event =>
                setPreferences(current => ({ ...current, [key]: event.target.checked }))
              }
            />
          </label>
        ))}
      </div>
      <button className="secondary-button" disabled={saving} onClick={() => void save()}>
        {saving ? "Enregistrement…" : "Enregistrer mes choix"}
      </button>
    </section>
  );
}

function ExternalAccessPortal() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [code, setCode] = useState("");
  const [access, setAccess] = useState<{
    id: string;
    spaceId: string;
    displayName: string;
    preciseLocationRequired: boolean;
    maximumLocationAccuracyMeters: number;
  }>();
  const [status, setStatus] = useState<"ready" | "checking" | "location" | "granted" | "error">("ready");
  const [message, setMessage] = useState("");
  const [items, setItems] = useState<ItemApiRow[]>();
  const [itemsLoading, setItemsLoading] = useState(false);

  async function verifyAccess() {
    setStatus("checking");
    setMessage("");
    const response = await fetch("/api/v1/access/consume", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token, code: code || undefined }),
    });
    if (!response.ok) {
      setStatus("error");
      setMessage("Le lien, le code ou sa période de validité ne permet pas l’accès.");
      return;
    }
    const result = (await response.json()) as { access: NonNullable<typeof access> };
    setAccess(result.access);
    setStatus(result.access.preciseLocationRequired ? "location" : "granted");
  }

  async function activatePreciseLocation() {
    if (!access) return;
    setStatus("checking");
    setMessage("Recherche d’une position précise…");
    navigator.geolocation.getCurrentPosition(
      position => {
        void (async () => {
          const coordinates = position.coords;
          const response = await fetch("/api/v1/access/location", {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              spaceId: access.spaceId,
              grantId: access.id,
              latitude: coordinates.latitude,
              longitude: coordinates.longitude,
              accuracyMeters: coordinates.accuracy,
              altitudeMeters: coordinates.altitude,
              altitudeAccuracyMeters: coordinates.altitudeAccuracy,
              headingDegrees: coordinates.heading,
              speedMetersPerSecond: coordinates.speed,
              capturedAt: new Date(position.timestamp).toISOString(),
              permissionState: "granted",
              policyVersion: "2026-07-25",
              explicitActivation: true,
            }),
          });
          if (!response.ok) {
            const result = (await response.json()) as {
              maximumAccuracyMeters?: number;
              receivedAccuracyMeters?: number;
            };
            setStatus("location");
            setMessage(
              result.receivedAccuracyMeters
                ? `Position trop imprécise (${Math.round(result.receivedAccuracyMeters)} m). Maximum permis : ${result.maximumAccuracyMeters} m.`
                : "La preuve de localisation n’a pas pu être validée."
            );
            return;
          }
          setStatus("granted");
          setMessage(`Position vérifiée à ±${Math.round(coordinates.accuracy)} m.`);
        })();
      },
      () => {
        setStatus("location");
        setMessage("La localisation précise est obligatoire pour cet espace. L’accès demeure verrouillé.");
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 }
    );
  }

  async function openExternalSpace() {
    setItemsLoading(true);
    setMessage("");
    try {
      const response = await fetch("/api/v1/access/items", {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("external_items_failed");
      const payload = (await response.json()) as { value: ItemApiRow[] };
      setItems(payload.value);
    } catch {
      setMessage("Les documents autorisés n’ont pas pu être chargés.");
    } finally {
      setItemsLoading(false);
    }
  }

  async function openExternalDocument(item: ItemApiRow) {
    const response = await fetch(
      `/api/v1/access/documents/${item.id}/url?disposition=inline`,
      { credentials: "include", cache: "no-store" }
    );
    if (!response.ok) {
      setMessage("Ce document ne peut pas être ouvert avec cette invitation.");
      return;
    }
    const { url } = (await response.json()) as { url: string };
    window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="auth-screen external-access">
      <div className="auth-card">
        <Brand light />
        <ShieldCheck size={34} />
        <h1>Accès sécurisé au document</h1>
        {items ? (
          <div className="external-document-list">
            <p>Documents autorisés pour {access?.displayName}.</p>
            {!items.length ? (
              <div className="empty-search">
                <Folder size={26} />
                <strong>Aucun document partagé</strong>
              </div>
            ) : (
              items.map(item => (
                <button key={item.id} onClick={() => void openExternalDocument(item)}>
                  <FileIcon type={documentType(item)} />
                  <span><strong>{item.name}</strong><small>{formatBytes(item.sizeBytes)}</small></span>
                  <ChevronRight size={16} />
                </button>
              ))
            )}
          </div>
        ) : !access ? (
          <>
            <p>Vérifiez votre invitation KOLIVO™ Vault. Le code n’est requis que si l’expéditeur a activé cette protection.</p>
            <label className="external-code">
              Code de sécurité
              <input value={code} onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000 000" />
            </label>
            <button className="primary-button" disabled={!token || status === "checking"} onClick={() => void verifyAccess()}>
              Vérifier mon accès
            </button>
          </>
        ) : status === "granted" ? (
          <>
            <p>{message || `Bienvenue ${access.displayName}. Votre accès est vérifié.`}</p>
            <button className="primary-button" disabled={itemsLoading} onClick={() => void openExternalSpace()}>
              {itemsLoading ? "Chargement…" : "Ouvrir l’espace documentaire"}
            </button>
          </>
        ) : (
          <>
            <div className="location-disclosure">
              <Globe2 size={22} />
              <p>
                Cet espace exige votre position GPS précise. Nous enregistrerons
                latitude, longitude, précision, heure, appareil et preuve
                d’activation. Aucun contenu de document ni caméra n’est capté.
              </p>
            </div>
            {message && <p className="access-message">{message}</p>}
            <button className="primary-button" disabled={status === "checking"} onClick={() => void activatePreciseLocation()}>
              Activer la localisation précise
            </button>
          </>
        )}
        {status === "error" && <p className="access-message error">{message}</p>}
      </div>
    </div>
  );
}

type ConsoleSection =
  | "overview"
  | "spaces"
  | "access"
  | "approvals"
  | "signatures"
  | "audit"
  | "templates"
  | "settings"
  | "help";

type AccessGrantRow = {
  id: string;
  spaceId: string;
  spaceName: string;
  email: string;
  displayName: string;
  role: string;
  accessMode: string;
  expiresAt: string;
  revokedAt?: string | null;
  useCount: number;
  maxUses: number;
};

type WorkflowRow = {
  id: string;
  spaceId: string;
  spaceName: string;
  documentName?: string | null;
  workflowType: "approval" | "signature" | "review";
  status: string;
  dueAt?: string | null;
  stepCount: number;
  completedStepCount: number;
  createdAt: string;
};

type AuditRow = {
  id: string;
  sequenceNumber: number;
  eventType: string;
  result: string;
  actorType: string;
  actorId?: string | null;
  resourceType?: string | null;
  occurredAt: string;
  eventHash: string;
};

const consoleNavigation: Array<{
  section: ConsoleSection;
  label: string;
  icon: typeof LayoutDashboard;
}> = [
  { section: "overview", label: "Vue d’ensemble", icon: LayoutDashboard },
  { section: "spaces", label: "Espaces clients", icon: FolderKanban },
  { section: "access", label: "Accès externes", icon: Users },
  { section: "approvals", label: "Approbations", icon: FileCheck2 },
  { section: "signatures", label: "Signatures", icon: FileSignature },
  { section: "audit", label: "Audit et conformité", icon: Activity },
  { section: "templates", label: "Modèles et règles", icon: SlidersHorizontal },
];

function sectionFromPath(pathname: string): ConsoleSection {
  const segment = pathname.split("/").filter(Boolean)[1];
  return consoleNavigation.some(item => item.section === segment)
    ? (segment as ConsoleSection)
    : segment === "settings" || segment === "help"
      ? segment
      : "overview";
}

function routeFor(section: ConsoleSection) {
  return `/app/${section}`;
}

function ProductionSpaceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const { accessToken } = useHaloId();
  const [status, setStatus] = useState<"idle" | "saving" | "error">("idle");
  const [loginRequired, setLoginRequired] = useState(true);
  const [preciseLocationRequired, setPreciseLocationRequired] = useState(false);
  return (
    <ModalShell
      title="Créer un espace documentaire"
      description="L’espace sera créé dans l’organisation HaloID active et utilisera le stockage sécurisé configuré."
      onClose={onClose}
    >
      <form
        className="form-grid"
        onSubmit={event => {
          event.preventDefault();
          setStatus("saving");
          const form = new FormData(event.currentTarget);
          const name = String(form.get("name") || "").trim();
          const slug = name
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "")
            .slice(0, 80);
          void (async () => {
            const token = await accessToken();
            const response = await fetch("/api/v1/spaces", {
              method: "POST",
              headers: {
                authorization: `Bearer ${token}`,
                "content-type": "application/json",
              },
              body: JSON.stringify({
                name,
                slug,
                templateKey: String(form.get("templateKey") || "client-standard"),
                region: "canadaeast",
                defaultRetentionDays: Number(form.get("retention") || 2555),
                loginRequired,
                haloIdSubjects: [],
                preciseLocationRequired,
                maximumLocationAccuracyMeters: Number(
                  form.get("maximumAccuracy") || 100
                ),
                locationLegalBasis: preciseLocationRequired
                  ? String(form.get("legalBasis") || "")
                  : undefined,
              }),
            });
            if (!response.ok) throw new Error("space_create_failed");
            await onCreated();
            onClose();
          })().catch(() => setStatus("error"));
        }}
      >
        <label className="full-row">
          Nom de l’espace
          <input name="name" required autoFocus minLength={2} maxLength={120} />
        </label>
        <label>
          Modèle
          <select name="templateKey" defaultValue="client-standard">
            <option value="client-standard">Client standard</option>
            <option value="board">Conseil d’administration</option>
            <option value="audit">Audit et conformité</option>
          </select>
        </label>
        <label>
          Rétention
          <select name="retention" defaultValue="2555">
            <option value="365">1 an</option>
            <option value="2555">7 ans</option>
            <option value="3650">10 ans</option>
          </select>
        </label>
        <fieldset className="access-policy">
          <legend>Authentification</legend>
          <label>
            <input
              type="radio"
              checked={loginRequired}
              onChange={() => setLoginRequired(true)}
            />
            HaloID obligatoire
          </label>
          <label>
            <input
              type="radio"
              checked={!loginRequired}
              onChange={() => setLoginRequired(false)}
            />
            Invitations externes permises
          </label>
        </fieldset>
        <label className="location-policy full-row">
          <input
            type="checkbox"
            checked={preciseLocationRequired}
            onChange={event => setPreciseLocationRequired(event.target.checked)}
          />
          <span>
            <strong>Exiger une localisation précise</strong>
            <small>L’accès externe demeure verrouillé sans preuve conforme.</small>
          </span>
        </label>
        {preciseLocationRequired && (
          <>
            <label>
              Précision maximale
              <select name="maximumAccuracy" defaultValue="100">
                <option value="25">25 mètres</option>
                <option value="50">50 mètres</option>
                <option value="100">100 mètres</option>
              </select>
            </label>
            <label>
              Base réglementaire
              <input name="legalBasis" required minLength={3} />
            </label>
          </>
        )}
        {status === "error" && (
          <p className="access-message error full-row">
            La création a échoué. Vérifiez le nom et réessayez.
          </p>
        )}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Annuler
          </button>
          <button className="primary-button" disabled={status === "saving"}>
            {status === "saving" ? "Création…" : "Créer l’espace"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function WorkflowModal({
  type,
  spaces,
  documents,
  activeSpaceId,
  onClose,
  onCreated,
}: {
  type: "approval" | "signature";
  spaces: SpaceSummary[];
  documents: DocumentSummary[];
  activeSpaceId?: string;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const { accessToken, user } = useHaloId();
  const [error, setError] = useState("");
  return (
    <ModalShell
      title={type === "approval" ? "Demander une approbation" : "Demander une signature"}
      description="La demande sera inscrite au workflow et au journal d’audit."
      onClose={onClose}
    >
      <form
        className="form-grid"
        onSubmit={event => {
          event.preventDefault();
          setError("");
          const form = new FormData(event.currentTarget);
          void (async () => {
            const token = await accessToken();
            const response = await fetch("/api/v1/workflows", {
              method: "POST",
              headers: {
                authorization: `Bearer ${token}`,
                "content-type": "application/json",
              },
              body: JSON.stringify({
                spaceId: String(form.get("spaceId")),
                documentItemId: form.get("documentItemId")
                  ? String(form.get("documentItemId"))
                  : undefined,
                workflowType: type,
                dueAt: form.get("dueAt")
                  ? new Date(String(form.get("dueAt"))).toISOString()
                  : undefined,
                assignees: [
                  {
                    subjectType: "haloid",
                    subjectId: String(form.get("assignee") || user?.sub || ""),
                    action: type === "signature" ? "sign" : "approve",
                  },
                ],
              }),
            });
            if (!response.ok) throw new Error("workflow_create_failed");
            await onCreated();
            onClose();
          })().catch(() =>
            setError("La demande n’a pas pu être créée. Vérifiez l’assignation.")
          );
        }}
      >
        <label>
          Espace
          <select name="spaceId" defaultValue={activeSpaceId} required>
            {spaces.map(space => (
              <option value={space.id} key={space.id}>{space.name}</option>
            ))}
          </select>
        </label>
        <label>
          Document
          <select name="documentItemId" defaultValue="">
            <option value="">Aucun document précis</option>
            {documents.map(document => (
              <option value={document.id} key={document.id}>{document.name}</option>
            ))}
          </select>
        </label>
        <label className="full-row">
          Identifiant HaloID de l’assigné
          <input name="assignee" required defaultValue={user?.sub || ""} />
        </label>
        <label className="full-row">
          Échéance
          <input name="dueAt" type="datetime-local" />
        </label>
        {error && <p className="access-message error full-row">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuler</button>
          <button className="primary-button">Créer la demande</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ProductionConsole() {
  const haloId = useHaloId();
  const [section, setSection] = useState<ConsoleSection>(() =>
    sectionFromPath(window.location.pathname)
  );
  const [adminMode, setAdminMode] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [spaces, setSpaces] = useState<SpaceSummary[]>([]);
  const [activeSpaceId, setActiveSpaceId] = useState<string>();
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [grants, setGrants] = useState<AccessGrantRow[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowRow[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"list" | "grid">("list");
  const [modal, setModal] = useState<
    "space" | "invite" | "upload" | "approval" | "signature" | null
  >(null);
  const [previewDocument, setPreviewDocument] = useState<DocumentSummary>();
  const [previewUrl, setPreviewUrl] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(true);
  const [installPrompt, setInstallPrompt] = useState<Event & {
    prompt: () => Promise<void>;
  }>();
  const activeSpace = spaces.find(space => space.id === activeSpaceId);
  const userName = haloId.user?.name || haloId.user?.email || "Utilisateur HaloID";

  const authorizedFetch = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const token = await haloId.accessToken();
      const response = await fetch(path, {
        ...init,
        headers: {
          authorization: `Bearer ${token}`,
          ...(init.body ? { "content-type": "application/json" } : {}),
          ...init.headers,
        },
        cache: "no-store",
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(payload.error || `request_${response.status}`);
      }
      return response;
    },
    [haloId.accessToken]
  );

  const loadSpaces = useCallback(async () => {
    const response = await authorizedFetch("/api/v1/spaces");
    const payload = (await response.json()) as { value: SpaceApiRow[] };
    const liveSpaces = payload.value.map(space => ({
      ...space,
      updatedAt: new Date(space.updatedAt).toLocaleString("fr-CA"),
      sharePointUrl: space.sharepointWebUrl || undefined,
    }));
    setSpaces(liveSpaces);
    setActiveSpaceId(current =>
      current && liveSpaces.some(space => space.id === current)
        ? current
        : liveSpaces[0]?.id
    );
  }, [authorizedFetch]);

  const loadDocuments = useCallback(async (spaceId?: string) => {
    if (!spaceId) {
      setDocuments([]);
      return;
    }
    const response = await authorizedFetch(`/api/v1/spaces/${spaceId}/items`);
    const payload = (await response.json()) as { value?: ItemApiRow[] };
    setDocuments(
      (payload.value || []).map(item => ({
        id: item.id,
        name: item.name,
        type: documentType(item),
        version: String(item.metadata?.version || "—"),
        size: formatBytes(item.sizeBytes),
        updatedAt: new Date(item.updatedAt).toLocaleString("fr-CA"),
        owner: String(item.metadata?.owner || "KOLIVO eDocs"),
        status: "draft",
        confidential: Boolean(item.metadata?.confidential),
      }))
    );
  }, [authorizedFetch]);

  const loadGrants = useCallback(async () => {
    const response = await authorizedFetch("/api/v1/access-grants");
    setGrants(((await response.json()) as { value: AccessGrantRow[] }).value);
  }, [authorizedFetch]);

  const loadWorkflows = useCallback(async () => {
    const response = await authorizedFetch("/api/v1/workflows");
    setWorkflows(((await response.json()) as { value: WorkflowRow[] }).value);
  }, [authorizedFetch]);

  const loadAudit = useCallback(async () => {
    const response = await authorizedFetch("/api/v1/audit?limit=250");
    setAudit(((await response.json()) as { value: AuditRow[] }).value);
  }, [authorizedFetch]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([loadSpaces(), loadGrants(), loadWorkflows(), loadAudit()]);
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message === "organization_context_required"
          ? "Votre session HaloID ne contient pas d’organisation."
          : "Certaines données réelles n’ont pas pu être chargées."
      );
    } finally {
      setLoading(false);
    }
  }, [loadSpaces, loadGrants, loadWorkflows, loadAudit]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    void loadDocuments(activeSpaceId).catch(() => setDocuments([]));
  }, [activeSpaceId, loadDocuments]);

  useEffect(() => {
    const pop = () => setSection(sectionFromPath(window.location.pathname));
    const install = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as Event & { prompt: () => Promise<void> });
    };
    window.addEventListener("popstate", pop);
    window.addEventListener("beforeinstallprompt", install);
    return () => {
      window.removeEventListener("popstate", pop);
      window.removeEventListener("beforeinstallprompt", install);
    };
  }, []);

  function navigate(next: ConsoleSection) {
    window.history.pushState({}, "", routeFor(next));
    setSection(next);
    setMobileMenu(false);
  }

  async function openDocument(document: DocumentSummary, disposition = "inline") {
    setPreviewDocument(document);
    setPreviewUrl("");
    try {
      const response = await authorizedFetch(
        `/api/v1/documents/${document.id}/url?disposition=${disposition}`
      );
      const { url } = (await response.json()) as { url: string };
      if (disposition === "attachment") {
        window.open(url, "_blank", "noopener,noreferrer");
        setPreviewDocument(undefined);
      } else {
        setPreviewUrl(url);
      }
    } catch {
      setPreviewUrl("error");
    }
  }

  async function revokeGrant(id: string) {
    if (!window.confirm("Révoquer immédiatement cet accès externe?")) return;
    await authorizedFetch(`/api/v1/access-grants/${id}/revoke`, {
      method: "POST",
      body: "{}",
    });
    await Promise.all([loadGrants(), loadAudit(), loadSpaces()]);
  }

  async function decideWorkflow(id: string, decision: "approved" | "cancelled") {
    await authorizedFetch(`/api/v1/workflows/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ decision }),
    });
    await Promise.all([loadWorkflows(), loadAudit()]);
  }

  async function exportAudit() {
    const response = await authorizedFetch("/api/v1/audit/export.csv");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `kolivo-edocs-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const filteredDocuments = documents.filter(document =>
    document.name.toLocaleLowerCase("fr").includes(query.toLocaleLowerCase("fr"))
  );
  const filteredSpaces = spaces.filter(space =>
    `${space.name} ${space.client}`.toLocaleLowerCase("fr").includes(query.toLocaleLowerCase("fr"))
  );
  const visibleNavigation = adminMode
    ? consoleNavigation
    : consoleNavigation.filter(item =>
        ["overview", "spaces", "approvals", "signatures"].includes(item.section)
      );

  const renderSpaces = () => (
    <>
      <section className="page-heading">
        <div>
          <div className="eyebrow"><span className="live-dot" /> Données en temps réel</div>
          <h1>Espaces documentaires</h1>
          <p>{spaces.length} espace{spaces.length === 1 ? "" : "s"} dans l’organisation KOLIVO.</p>
        </div>
        {adminMode && (
          <button className="primary-button" onClick={() => setModal("space")}>
            <Plus size={17} /> Nouvel espace
          </button>
        )}
      </section>
      {!spaces.length ? (
        <section className="documents-card empty-search">
          <FolderKanban size={34} />
          <strong>Aucun espace documentaire</strong>
          <span>Créez le premier espace ici ou depuis KOLIVO Gate.</span>
          {adminMode && (
            <button className="primary-button" onClick={() => setModal("space")}>
              <Plus size={16} /> Créer le premier espace
            </button>
          )}
        </section>
      ) : (
        <>
          <section className="space-cards production-space-grid">
            {filteredSpaces.map(space => (
              <button
                className={space.id === activeSpaceId ? "selected" : ""}
                key={space.id}
                onClick={() => setActiveSpaceId(space.id)}
              >
                <span className="space-monogram">{initials(space.client)}</span>
                <span>
                  <strong>{space.name}</strong>
                  <small>{space.client} · {space.files} document{space.files === 1 ? "" : "s"}</small>
                </span>
                <span className={`space-state ${space.status}`}>{space.status}</span>
                <ChevronRight size={17} />
              </button>
            ))}
          </section>
          {activeSpace && (
            <section className="documents-card workspace-card">
              <div className="workspace-card-heading">
                <div>
                  <span className="eyebrow">Espace sélectionné</span>
                  <h2>{activeSpace.name}</h2>
                </div>
                <div className="heading-actions">
                  <button className="secondary-button" onClick={() => setModal("invite")}>
                    <UserPlus size={16} /> Inviter
                  </button>
                  <button className="primary-button" onClick={() => setModal("upload")}>
                    <Upload size={16} /> Téléverser
                  </button>
                </div>
              </div>
              <div className="document-toolbar">
                <div className="document-search">
                  <Search size={17} />
                  <input
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    placeholder="Chercher dans cet espace"
                    aria-label="Chercher dans les documents"
                  />
                </div>
                <button className="filter-button" onClick={() => setFilterOpen(value => !value)}>
                  <SlidersHorizontal size={16} /> {filterOpen ? "Masquer les filtres" : "Filtres"}
                </button>
                <div className="view-toggle">
                  <button className={view === "list" ? "active" : ""} onClick={() => setView("list")} aria-label="Vue liste"><List size={17} /></button>
                  <button className={view === "grid" ? "active" : ""} onClick={() => setView("grid")} aria-label="Vue grille"><Grid2X2 size={17} /></button>
                </div>
              </div>
              {filterOpen && (
                <div className="active-filter">
                  <ShieldCheck size={15} />
                  Seuls les documents actifs et autorisés pour votre organisation sont affichés.
                  <button onClick={() => setQuery("")}>Réinitialiser</button>
                </div>
              )}
              <div className={`document-list ${view}`}>
                <div className="document-head">
                  <span>Nom</span><span>Version</span><span>Modifié</span><span>État</span><span />
                </div>
                {filteredDocuments.map(document => (
                  <button
                    className="document-row"
                    key={document.id}
                    onClick={() => void openDocument(document)}
                  >
                    <span className={`file-cell ${document.type}`}>
                      <span className="file-icon"><FileIcon type={document.type} /></span>
                      <span><strong>{document.name}</strong><small>{document.owner} · {document.size}</small></span>
                    </span>
                    <span>{document.version}</span>
                    <span>{document.updatedAt}</span>
                    <span><em className="status draft">Disponible</em></span>
                    <span><Eye size={17} /></span>
                  </button>
                ))}
                {!filteredDocuments.length && (
                  <div className="empty-search">
                    <Folder size={26} />
                    <strong>{query ? "Aucun résultat" : "Cet espace est vide"}</strong>
                    <span>{query ? "Modifiez votre recherche." : "Utilisez Téléverser pour ajouter un document."}</span>
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </>
  );

  const workflowList = (type: "approval" | "signature") => {
    const rows = workflows.filter(workflow => workflow.workflowType === type);
    return (
      <>
        <section className="page-heading">
          <div>
            <div className="eyebrow"><span className="live-dot" /> Workflow vérifiable</div>
            <h1>{type === "approval" ? "Approbations" : "Signatures"}</h1>
            <p>{rows.length} demande{rows.length === 1 ? "" : "s"} réelle{rows.length === 1 ? "" : "s"}.</p>
          </div>
          <button
            className="primary-button"
            disabled={!spaces.length}
            onClick={() => setModal(type)}
          >
            <Plus size={16} /> Nouvelle demande
          </button>
        </section>
        <section className="documents-card data-table-card">
          {!rows.length ? (
            <div className="empty-search">
              {type === "approval" ? <FileCheck2 size={30} /> : <FileSignature size={30} />}
              <strong>Aucune demande</strong>
              <span>Les demandes créées apparaîtront ici.</span>
            </div>
          ) : rows.map(workflow => (
            <div className="management-row" key={workflow.id}>
              <span className="row-icon">{type === "approval" ? <FileCheck2 /> : <FileSignature />}</span>
              <span>
                <strong>{workflow.documentName || workflow.spaceName}</strong>
                <small>{workflow.completedStepCount}/{workflow.stepCount} étape(s) · {new Date(workflow.createdAt).toLocaleString("fr-CA")}</small>
              </span>
              <em className={`status ${workflow.status === "approved" ? "approved" : "review"}`}>{workflow.status}</em>
              {workflow.status === "pending" && (
                <div className="row-actions">
                  <button className="secondary-button" onClick={() => void decideWorkflow(workflow.id, "cancelled")}>Annuler</button>
                  <button className="primary-button" onClick={() => void decideWorkflow(workflow.id, "approved")}>Approuver</button>
                </div>
              )}
            </div>
          ))}
        </section>
      </>
    );
  };

  const renderAccess = () => (
    <>
      <section className="page-heading">
        <div>
          <div className="eyebrow"><span className="live-dot" /> Contrôle d’accès</div>
          <h1>Accès externes</h1>
          <p>Expiration, usages et révocation en temps réel.</p>
        </div>
        <button className="primary-button" disabled={!activeSpace} onClick={() => setModal("invite")}>
          <UserPlus size={16} /> Nouvelle invitation
        </button>
      </section>
      <section className="documents-card data-table-card">
        {!grants.length ? (
          <div className="empty-search"><KeyRound size={30} /><strong>Aucun accès externe</strong><span>Créez une invitation depuis un espace documentaire.</span></div>
        ) : grants.map(grant => (
          <div className="management-row" key={grant.id}>
            <span className="row-icon"><KeyRound /></span>
            <span>
              <strong>{grant.displayName}</strong>
              <small>{grant.email} · {grant.spaceName} · {grant.useCount}/{grant.maxUses} utilisation(s)</small>
            </span>
            <em className={`status ${grant.revokedAt ? "review" : "approved"}`}>
              {grant.revokedAt ? "Révoqué" : new Date(grant.expiresAt) < new Date() ? "Expiré" : "Actif"}
            </em>
            {!grant.revokedAt && (
              <button className="secondary-button" onClick={() => void revokeGrant(grant.id)}>Révoquer</button>
            )}
          </div>
        ))}
      </section>
    </>
  );

  const renderAudit = () => (
    <>
      <section className="page-heading">
        <div>
          <div className="eyebrow"><span className="live-dot" /> Chaîne de hachage immuable</div>
          <h1>Audit et conformité</h1>
          <p>{audit.length} événement{audit.length === 1 ? "" : "s"} chargé{audit.length === 1 ? "" : "s"}.</p>
        </div>
        <button className="secondary-button" onClick={() => void exportAudit()}>
          <Download size={16} /> Exporter CSV
        </button>
      </section>
      <section className="documents-card data-table-card">
        {!audit.length ? (
          <div className="empty-search"><Activity size={30} /><strong>Aucun événement</strong><span>Les actions vérifiées apparaîtront ici.</span></div>
        ) : audit.map(event => (
          <div className="management-row audit-row" key={event.id}>
            <span className="sequence">#{event.sequenceNumber}</span>
            <span>
              <strong>{event.eventType}</strong>
              <small>{event.actorType} · {new Date(event.occurredAt).toLocaleString("fr-CA")}</small>
            </span>
            <em className={`status ${event.result === "success" ? "approved" : "review"}`}>{event.result}</em>
            <code title={event.eventHash}>{event.eventHash.slice(0, 12)}…</code>
          </div>
        ))}
      </section>
    </>
  );

  const content = section === "spaces"
    ? renderSpaces()
    : section === "access"
      ? renderAccess()
      : section === "approvals"
        ? workflowList("approval")
        : section === "signatures"
          ? workflowList("signature")
          : section === "audit"
            ? renderAudit()
            : section === "templates"
              ? (
                <>
                  <section className="page-heading"><div><div className="eyebrow"><span className="live-dot" /> Politiques actives</div><h1>Modèles et règles</h1><p>Configuration utilisée par les nouveaux espaces.</p></div></section>
                  <section className="policy-grid">
                    {[
                      ["Client standard", "Rétention 7 ans · téléchargement contrôlé"],
                      ["Conseil d’administration", "Watermark · approbation · accès limité"],
                      ["Audit et conformité", "Localisation précise optionnelle · journal immuable"],
                    ].map(([title, copy]) => (
                      <article className="documents-card" key={title}><ShieldCheck /><strong>{title}</strong><p>{copy}</p></article>
                    ))}
                  </section>
                </>
              )
              : section === "settings"
                ? (
                  <>
                    <section className="page-heading"><div><h1>Paramètres</h1><p>Session, application et installation.</p></div></section>
                    <section className="settings-grid">
                      <article className="documents-card"><Brand light /><strong>{userName}</strong><p>Session protégée par HaloID.</p><button className="secondary-button" onClick={() => void haloId.logout()}>Se déconnecter</button></article>
                      <article className="documents-card"><HardDrive /><strong>Application installable</strong><p>Installez eDocs sur macOS, iPhone, iPad, Android ou Windows.</p><button className="primary-button" disabled={!installPrompt} onClick={() => void installPrompt?.prompt()}>{installPrompt ? "Installer eDocs" : "Installation gérée par le navigateur"}</button></article>
                    </section>
                  </>
                )
                : section === "help"
                  ? (
                    <>
                      <section className="page-heading"><div><h1>Centre d’aide</h1><p>Assistance KOLIVO eDocs.</p></div></section>
                      <section className="documents-card help-card"><CircleHelp size={34} /><strong>Besoin d’aide?</strong><p>Consultez l’état du service ou communiquez avec l’équipe KOLIVO.</p><div className="heading-actions"><a className="secondary-button" href="/health" target="_blank" rel="noreferrer">État du service</a><a className="primary-button" href="mailto:support@kolivo.ca?subject=Assistance%20KOLIVO%20eDocs">Écrire au soutien</a></div></section>
                    </>
                  )
                  : (
                    <>
                      <section className="page-heading">
                        <div><div className="eyebrow"><span className="live-dot" /> Production Canada</div><h1>Bonjour {userName.split(" ")[0]}</h1><p>Vue d’ensemble de votre environnement documentaire réel.</p></div>
                        <button className="primary-button" onClick={() => navigate("spaces")}><FolderKanban size={16} /> Ouvrir les espaces</button>
                      </section>
                      <section className="overview-grid">
                        <button className="metric-card documents-card" onClick={() => navigate("spaces")}><FolderKanban /><span><strong>{spaces.length}</strong><small>Espaces</small></span></button>
                        <button className="metric-card documents-card" onClick={() => navigate("access")}><Users /><span><strong>{grants.filter(grant => !grant.revokedAt).length}</strong><small>Accès externes</small></span></button>
                        <button className="metric-card documents-card" onClick={() => navigate("approvals")}><FileCheck2 /><span><strong>{workflows.filter(workflow => workflow.status === "pending").length}</strong><small>Actions en attente</small></span></button>
                        <button className="metric-card documents-card" onClick={() => navigate("audit")}><Activity /><span><strong>{audit.length}</strong><small>Événements récents</small></span></button>
                      </section>
                      <section className="documents-card empty-search overview-empty">
                        {spaces.length ? <><ShieldCheck size={30} /><strong>Système opérationnel</strong><span>Les espaces et contrôles sont chargés depuis la production.</span></> : <><FolderKanban size={30} /><strong>Prêt pour votre premier espace</strong><span>Aucune donnée de démonstration n’est affichée.</span><button className="primary-button" onClick={() => setModal("space")}><Plus size={16} /> Créer un espace</button></>}
                      </section>
                    </>
                  );

  return (
    <div className="app-shell">
      {mobileMenu && <button className="scrim" aria-label="Fermer le menu" onClick={() => setMobileMenu(false)} />}
      <aside className={`sidebar production-sidebar ${mobileMenu ? "is-open" : ""}`}>
        <Brand />
        <div className="mode-switch" role="group" aria-label="Mode d’affichage">
          <button className={!adminMode ? "active" : ""} onClick={() => { setAdminMode(false); navigate("overview"); }}>Client</button>
          <button className={adminMode ? "active" : ""} onClick={() => setAdminMode(true)}>Administration</button>
        </div>
        <nav aria-label="Navigation principale">
          <div className="nav-label">{adminMode ? "Gérer eDocs" : "Mon espace"}</div>
          {visibleNavigation.map(item => {
            const Icon = item.icon;
            return (
              <button className={`nav-item ${section === item.section ? "active" : ""}`} key={item.section} onClick={() => navigate(item.section)}>
                <Icon size={18} /><span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <button className={`nav-item ${section === "help" ? "active" : ""}`} onClick={() => navigate("help")}><CircleHelp size={18} /><span>Centre d’aide</span></button>
          <button className={`nav-item ${section === "settings" ? "active" : ""}`} onClick={() => navigate("settings")}><Settings size={18} /><span>Paramètres</span></button>
          <button className="user-card" onClick={() => navigate("settings")}>
            <span className="avatar">{initials(userName)}</span>
            <span><strong>{userName}</strong><small>HaloID</small></span>
            <ChevronRight size={16} />
          </button>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileMenu(true)} aria-label="Ouvrir le menu"><Menu /></button>
          <div className="global-search">
            <Search size={18} />
            <input value={query} onChange={event => setQuery(event.target.value)} aria-label="Rechercher partout" placeholder="Rechercher un espace ou un document…" />
            {query && <button className="search-clear" onClick={() => setQuery("")} aria-label="Effacer la recherche"><X size={15} /></button>}
          </div>
          <button className="icon-button notification" aria-label="Notifications" onClick={() => navigate("approvals")}><Bell size={19} />{workflows.some(workflow => workflow.status === "pending") && <span />}</button>
          <button className="organization-button" onClick={() => navigate("settings")}><div className="mini-logo">K</div><span>KOLIVO</span><ChevronDown size={16} /></button>
        </header>
        <div className="page">
          {loading ? (
            <section className="documents-card empty-search"><Clock3 className="spin" size={28} /><strong>Synchronisation sécurisée…</strong></section>
          ) : error ? (
            <section className="documents-card empty-search"><ShieldCheck size={28} /><strong>Connexion aux données interrompue</strong><span>{error}</span><button className="secondary-button" onClick={() => void refreshAll()}>Réessayer</button></section>
          ) : content}
        </div>
      </main>
      <nav className="mobile-bottom-nav" aria-label="Navigation mobile">
        {[
          ["overview", LayoutDashboard, "Accueil"],
          ["spaces", FolderKanban, "Espaces"],
          ["approvals", FileCheck2, "À faire"],
          ["settings", Settings, "Réglages"],
        ].map(([target, Icon, label]) => (
          <button
            key={target as string}
            className={section === target ? "active" : ""}
            onClick={() => navigate(target as ConsoleSection)}
          >
            <Icon size={20} />
            <span>{label as string}</span>
          </button>
        ))}
      </nav>
      {consentOpen && <ConsentPanel onClose={() => setConsentOpen(false)} />}
      {modal === "space" && <ProductionSpaceModal onClose={() => setModal(null)} onCreated={refreshAll} />}
      {modal === "invite" && activeSpace && <InviteModal spaceId={activeSpace.id} onClose={() => { setModal(null); void refreshAll(); }} />}
      {modal === "upload" && activeSpace && <UploadModal spaceId={activeSpace.id} onClose={() => { setModal(null); void loadDocuments(activeSpace.id); void loadSpaces(); }} />}
      {(modal === "approval" || modal === "signature") && (
        <WorkflowModal type={modal} spaces={spaces} documents={documents} activeSpaceId={activeSpaceId} onClose={() => setModal(null)} onCreated={loadWorkflows} />
      )}
      {previewDocument && (
        <ModalShell title={previewDocument.name} description="Aperçu sécurisé à durée limitée." onClose={() => setPreviewDocument(undefined)}>
          {previewUrl === "error" ? (
            <div className="empty-search"><File size={28} /><strong>Aperçu indisponible</strong><span>Ce format peut être téléchargé de façon sécurisée.</span></div>
          ) : previewUrl ? (
            <iframe className="document-preview" src={previewUrl} title={`Aperçu de ${previewDocument.name}`} />
          ) : (
            <div className="empty-search"><Clock3 className="spin" size={26} /><strong>Préparation de l’aperçu…</strong></div>
          )}
          <div className="modal-actions preview-actions">
            <button className="secondary-button" onClick={() => setPreviewDocument(undefined)}>Fermer</button>
            <button className="primary-button" onClick={() => void openDocument(previewDocument, "attachment")}><Download size={16} /> Télécharger</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function LegacyApp() {
  const haloId = useHaloId();
  const [adminMode, setAdminMode] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [spaces, setSpaces] = useState<SpaceSummary[]>([]);
  const [activeSpaceId, setActiveSpaceId] = useState<string>();
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string>();
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"list" | "grid">("list");
  const [modal, setModal] = useState<Modal>(null);
  const [consentOpen, setConsentOpen] = useState(true);
  const activeSpace = spaces.find(space => space.id === activeSpaceId);
  const filteredDocuments = useMemo(
    () =>
      documents.filter(document =>
        document.name.toLocaleLowerCase("fr").includes(query.toLocaleLowerCase("fr"))
      ),
    [query]
  );

  useEffect(() => {
    if (!haloId.authenticated) return;
    let active = true;
    setWorkspaceLoading(true);
    setWorkspaceError(undefined);
    void (async () => {
      const token = await haloId.accessToken();
      const response = await fetch("/api/v1/spaces", {
        headers: { authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `spaces_${response.status}`);
      }
      const payload = (await response.json()) as { value: SpaceApiRow[] };
      if (!active) return;
      const liveSpaces = payload.value.map(space => ({
        ...space,
        updatedAt: new Date(space.updatedAt).toLocaleString("fr-CA"),
        sharePointUrl: space.sharepointWebUrl || undefined,
      }));
      setSpaces(liveSpaces);
      setActiveSpaceId(current =>
        current && liveSpaces.some(space => space.id === current)
          ? current
          : liveSpaces[0]?.id
      );
    })()
      .catch(error => {
        if (active) {
          setWorkspaceError(
            error instanceof Error && error.message === "organization_context_required"
              ? "Votre session HaloID ne contient aucune organisation. Aucun espace ne peut être affiché."
              : "Les espaces réels n’ont pas pu être chargés."
          );
        }
      })
      .finally(() => {
        if (active) setWorkspaceLoading(false);
      });
    return () => {
      active = false;
    };
  }, [haloId.authenticated, haloId.accessToken]);

  useEffect(() => {
    if (!haloId.authenticated || !activeSpaceId) {
      setDocuments([]);
      return;
    }
    let active = true;
    void (async () => {
      const token = await haloId.accessToken();
      const response = await fetch(`/api/v1/spaces/${activeSpaceId}/items`, {
        headers: { authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`items_${response.status}`);
      const payload = (await response.json()) as { value?: ItemApiRow[] };
      const rows = payload.value || [];
      if (!active) return;
      setDocuments(
        rows.map(item => ({
          id: item.id,
          name: item.name,
          type: documentType(item),
          version: String(item.metadata?.version || "—"),
          size: formatBytes(item.sizeBytes),
          updatedAt: new Date(item.updatedAt).toLocaleString("fr-CA"),
          owner: String(item.metadata?.owner || "KOLIVO eDocs"),
          status: "draft",
          confidential: Boolean(item.metadata?.confidential),
        }))
      );
    })().catch(() => {
      if (active) setDocuments([]);
    });
    return () => {
      active = false;
    };
  }, [activeSpaceId, haloId.authenticated, haloId.accessToken]);

  useEffect(() => {
    if (!modal) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") setModal(null);
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [modal]);

  if (window.location.pathname.startsWith("/access")) {
    return <ExternalAccessPortal />;
  }
  if (haloId.loading) {
    return <div className="auth-screen"><Brand /><span>Connexion sécurisée…</span></div>;
  }
  if (haloId.error || !haloId.configured) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <Brand light />
          <ShieldCheck size={34} />
          <h1>Connexion interrompue</h1>
          <p>{haloId.error || "HaloID n’est pas disponible pour le moment."}</p>
          <button className="primary-button" onClick={haloId.retry}>
            Réessayer la connexion
          </button>
        </div>
      </div>
    );
  }
  if (
    !haloId.authenticated &&
    !window.location.pathname.startsWith("/access")
  ) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <Brand light />
          <ShieldCheck size={34} />
          <h1>Vos documents, sous contrôle.</h1>
          <p>Connectez-vous avec votre identité HaloID pour accéder aux espaces autorisés.</p>
          <button className="primary-button" onClick={() => void haloId.login()}>
            Se connecter avec HaloID
          </button>
        </div>
      </div>
    );
  }

  const userName =
    haloId.user?.name || haloId.user?.email || "Utilisateur HaloID";

  if (workspaceLoading || workspaceError || !activeSpace) {
    return (
      <div className="app-shell">
        <Sidebar
          adminMode={adminMode}
          onAdminMode={setAdminMode}
          open={mobileMenu}
          onClose={() => setMobileMenu(false)}
          userName={userName}
        />
        <main className="main">
          <header className="topbar">
            <button className="icon-button mobile-menu" onClick={() => setMobileMenu(true)} aria-label="Ouvrir le menu"><Menu /></button>
            <div className="global-search">
              <Search size={18} />
              <input disabled aria-label="Rechercher partout" placeholder="Rechercher un document ou un espace…" />
            </div>
            <button className="organization-button">
              <div className="mini-logo">K</div>
              <span>KOLIVO eDocs</span>
            </button>
          </header>
          <div className="page">
            <section className="documents-card empty-search" aria-live="polite">
              {workspaceLoading ? (
                <>
                  <Clock3 size={30} />
                  <strong>Chargement des espaces réels…</strong>
                  <span>eDocs interroge votre organisation HaloID.</span>
                </>
              ) : workspaceError ? (
                <>
                  <ShieldCheck size={30} />
                  <strong>Impossible d’afficher les espaces</strong>
                  <span>{workspaceError}</span>
                  <button className="secondary-button" onClick={() => window.location.reload()}>
                    Réessayer
                  </button>
                </>
              ) : (
                <>
                  <FolderKanban size={30} />
                  <strong>Aucun espace documentaire</strong>
                  <span>
                    Aucun espace réel n’est associé à votre organisation HaloID.
                    Les espaces créés depuis KOLIVO Gate apparaîtront ici.
                  </span>
                </>
              )}
            </section>
          </div>
        </main>
        {consentOpen && <ConsentPanel onClose={() => setConsentOpen(false)} />}
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Sidebar
        adminMode={adminMode}
        onAdminMode={setAdminMode}
        open={mobileMenu}
        onClose={() => setMobileMenu(false)}
        userName={userName}
      />
      <main className="main">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileMenu(true)} aria-label="Ouvrir le menu"><Menu /></button>
          <div className="global-search">
            <Search size={18} />
            <input aria-label="Rechercher partout" placeholder="Rechercher un document, un espace ou une personne…" />
            <kbd>⌘ K</kbd>
          </div>
          <button className="icon-button notification" aria-label="Notifications">
            <Bell size={19} /><span />
          </button>
          <button className="organization-button">
            <div className="mini-logo">{initials(activeSpace.client)}</div>
            <span>{activeSpace.client}</span>
            <ChevronDown size={16} />
          </button>
        </header>

        <div className="page">
          <div className="breadcrumb">
            <span>Espaces clients</span><ChevronRight size={14} /><strong>{activeSpace.name}</strong>
          </div>
          <section className="page-heading">
            <div>
              <div className="eyebrow">
                <span className="live-dot" />
                {activeSpace.status === "active" ? "Espace actif" : `État : ${activeSpace.status}`}
              </div>
              <h1>{activeSpace.name}</h1>
              <p>{activeSpace.client} · mis à jour {activeSpace.updatedAt}</p>
            </div>
            <div className="heading-actions">
              <button className="secondary-button" onClick={() => setModal("invite")}><UserPlus size={17} /> Inviter</button>
              <button className="primary-button" onClick={() => setModal("upload")}><Upload size={17} /> Téléverser</button>
            </div>
          </section>

          <section className="trust-strip">
            <div><ShieldCheck /><span><strong>Protection eDocs</strong>Contrôles appliqués selon la politique de l’espace</span></div>
            <div><Users /><span><strong>{activeSpace.externalUsers} accès externes actifs</strong>Données calculées en temps réel</span></div>
            <div><FileCheck2 /><span><strong>{activeSpace.files} documents</strong>Aucun contenu fictif affiché</span></div>
            <button onClick={() => setConsentOpen(true)}>Confidentialité <ChevronRight size={15} /></button>
          </section>

          <div className="content-grid">
            <section className="documents-card">
              <div className="section-tabs">
                <button className="active">Documents</button>
                <button>Approbations</button>
                <button>Activité</button>
                <button>Accès</button>
              </div>
              <div className="document-toolbar">
                <div className="document-search"><Search size={17} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Chercher dans cet espace" aria-label="Chercher dans les documents" /></div>
                <button className="filter-button"><SlidersHorizontal size={16} /> Filtres</button>
                <div className="view-toggle">
                  <button className={view === "list" ? "active" : ""} onClick={() => setView("list")} aria-label="Vue liste"><List size={17} /></button>
                  <button className={view === "grid" ? "active" : ""} onClick={() => setView("grid")} aria-label="Vue grille"><Grid2X2 size={17} /></button>
                </div>
              </div>
              <div className="folder-path"><button><HardDrive size={15} /> Documents</button><ChevronRight size={14} /><strong>{activeSpace.name}</strong></div>
              <div className={`document-list ${view}`}>
                <div className="document-head"><span>Nom</span><span>Version</span><span>Modifié</span><span>État</span><span /></div>
                {filteredDocuments.map(document => (
                  <button className="document-row" key={document.id}>
                    <span className={`file-cell ${document.type}`}>
                      <span className="file-icon"><FileIcon type={document.type} /></span>
                      <span><strong>{document.name}</strong><small>{document.owner} · {document.size}</small></span>
                      {document.confidential && <LockKeyhole size={13} aria-label="Confidentiel" />}
                    </span>
                    <span>{document.version}</span>
                    <span>{document.updatedAt}</span>
                    <span><em className={`status ${document.status}`}>{statusLabel[document.status]}</em></span>
                    <span><MoreHorizontal size={17} /></span>
                  </button>
                ))}
                {!filteredDocuments.length && (
                  <div className="empty-search">
                    <Folder size={26} />
                    <strong>{query ? "Aucun document trouvé" : "Aucun document dans cet espace"}</strong>
                    <span>{query ? "Essayez un autre terme." : "Téléversez le premier document lorsque l’espace est prêt."}</span>
                  </div>
                )}
              </div>
              <div className="document-footer"><span>{filteredDocuments.length} élément{filteredDocuments.length === 1 ? "" : "s"}</span></div>
            </section>

            <aside className="right-rail">
              <section className="quick-card">
                <div className="card-heading"><div><strong>Actions</strong><span>Pour cet espace réel</span></div><Sparkles size={18} /></div>
                <button onClick={() => setModal("upload")}><span className="action-icon amber"><Upload /></span><span><strong>Téléverser un document</strong><small>Stockage sécurisé configuré</small></span><ChevronRight /></button>
                <button onClick={() => setModal("invite")}><span className="action-icon blue"><UserPlus /></span><span><strong>Inviter un partenaire</strong><small>Lien magique sécurisé</small></span><ChevronRight /></button>
              </section>
              <section className="activity-card">
                <div className="card-heading"><div><strong>Activité récente</strong><span>Journal réel seulement</span></div></div>
                <div className="empty-search">
                  <Activity size={22} />
                  <strong>Aucun événement affiché</strong>
                  <span>Les événements d’audit seront présentés ici lorsqu’ils seront disponibles.</span>
                </div>
              </section>
            </aside>
          </div>

          <section className="spaces-section">
            <div className="spaces-title"><div><h2>Autres espaces clients</h2><p>Passez d’un espace réel à l’autre sans quitter eDocs.</p></div></div>
            <div className="space-cards">
              {spaces.filter(space => space.id !== activeSpace.id).map(space => (
                <button key={space.id} onClick={() => setActiveSpaceId(space.id)}>
                  <span className="space-monogram">{space.client.split(" ").map(word => word[0]).join("").slice(0, 2)}</span>
                  <span><strong>{space.name}</strong><small>{space.client} · {space.files} documents</small></span>
                  <span className={`space-state ${space.status}`}>{space.status === "attention" ? "Action requise" : "À jour"}</span>
                  <ChevronRight size={17} />
                </button>
              ))}
            </div>
          </section>
        </div>
      </main>
      {consentOpen && <ConsentPanel onClose={() => setConsentOpen(false)} />}
      {modal === "invite" && <InviteModal spaceId={activeSpace.id} onClose={() => setModal(null)} />}
      {modal === "upload" && (
        <UploadModal spaceId={activeSpace.id} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

export function App() {
  const haloId = useHaloId();
  if (window.location.pathname.startsWith("/access")) {
    return <ExternalAccessPortal />;
  }
  if (haloId.loading) {
    return (
      <div className="auth-screen">
        <Brand light />
        <span>Connexion sécurisée…</span>
      </div>
    );
  }
  if (haloId.error || !haloId.configured) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <Brand light />
          <ShieldCheck size={34} />
          <h1>Connexion interrompue</h1>
          <p>{haloId.error || "HaloID n’est pas disponible pour le moment."}</p>
          <button className="primary-button" onClick={haloId.retry}>
            Réessayer la connexion
          </button>
        </div>
      </div>
    );
  }
  if (!haloId.authenticated) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <Brand light />
          <ShieldCheck size={34} />
          <h1>Vos documents, sous contrôle.</h1>
          <p>
            Connectez-vous avec votre identité HaloID pour accéder aux espaces
            autorisés.
          </p>
          <button className="primary-button" onClick={() => void haloId.login()}>
            Se connecter avec HaloID
          </button>
        </div>
      </div>
    );
  }
  return <ProductionConsole />;
}
